/**
 * CRM master-list admin sections — Contacts + Lists.
 *
 * Rendered inside AdminEmailManagement.tsx as the 'contacts' and 'lists'
 * subsections. Backed by /api/admin/crm (routers/admin_crm.py).
 *
 * Contacts = the unified master list (app users mirrored in + non-user
 * subscribers). "User" badge = linked_user_id IS NOT NULL.
 * Lists = saved static (hand-picked) or dynamic (saved-filter) audiences.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Tag, Search, X, ChevronLeft, ChevronRight, Trash2, Plus, Send, Mail } from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';

// ── Tokens (mirrors AdminEmailManagement) ───────────────────────────────────
const C = {
  primary: '#1F6C4F', primaryLight: '#5FA287', subtle: '#E8F2ED',
  limestone: '#F3F3F3', surface: '#FFFFFF', ink: '#0B1614', text: '#374151',
  textSecondary: '#6B7280', textTertiary: '#9CA3AF', border: '#E5E7EB',
  borderLight: '#F3F4F6', info: '#1E40AF', infoBg: '#DBEAFE',
  success: '#065F46', successBg: '#D1FAE5', error: '#DC2626', errorBg: '#FEE2E2',
  purple: '#5B21B6', purpleBg: '#EDE9FE', warning: '#92400E', warningBg: '#FEF3C7',
};
const R = { sm: 6, md: 10, lg: 14, xl: 20, pill: 9999 };
const SH = { sm: '0px 1px 2px rgba(0,0,0,0.05)', lg: '0px 4px 12px rgba(0,0,0,0.15)' };

type Toast = (msg: string, type?: 'success' | 'error' | 'info') => void;

const Spinner: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
    <div style={{
      width: 28, height: 28, borderRadius: '50%', border: `3px solid ${C.limestone}`,
      borderTopColor: C.primary, animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Pill: React.FC<{ bg: string; color: string; children: React.ReactNode }> = ({ bg, color, children }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: R.pill, fontSize: 11,
    fontWeight: 600, background: bg, color, textTransform: 'uppercase', letterSpacing: '0.04em',
  }}>{children}</span>
);

const SOURCE_LABELS: Record<string, string> = {
  in_app: 'In-app', contact_form: 'Contact form', footer: 'Footer',
  blog: 'Blog', import: 'Import', admin: 'Admin', app_user: 'App user',
};

// ── Stat cards ───────────────────────────────────────────────────────────────
const StatCard: React.FC<{ label: string; value: number | string; accent?: string }> = ({ label, value, accent }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg,
    padding: '16px 20px', flex: 1, minWidth: 130,
  }}>
    <div style={{ fontSize: 26, fontWeight: 700, color: accent || C.ink, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 4, fontWeight: 500 }}>{label}</div>
  </div>
);

// ── Types ────────────────────────────────────────────────────────────────────
interface Contact {
  id: string; email: string; first_name: string; last_name: string;
  is_user: boolean; linked_user_id: string | null; newsletter_subscribed: boolean;
  newsletter_source: string | null; first_lead_source: string | null;
  last_inquiry_at: string | null; inquiry_count: number;
}
interface CrmStats {
  total_contacts: number; users: number; non_users: number; subscribed: number;
  subscribed_users: number; subscribed_non_users: number; total_lists: number;
}
interface ContactFilter {
  search?: string; is_user?: boolean | null; newsletter?: boolean | null; source?: string | null;
}

// ════════════════════════════════════════════════════════════════════════════
//  CONTACTS SECTION
// ════════════════════════════════════════════════════════════════════════════
export const CrmContactsSection: React.FC<{ onToast: Toast }> = ({ onToast }) => {
  const [stats, setStats] = useState<CrmStats | null>(null);
  const [filter, setFilter] = useState<ContactFilter>({});
  const [searchInput, setSearchInput] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [showSaveList, setShowSaveList] = useState(false);
  const LIMIT = 50;

  const loadStats = useCallback(async () => {
    try { setStats(await adminFetch<CrmStats>('/api/admin/crm/stats')); } catch { /* noop */ }
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filter.search) p.set('search', filter.search);
      if (filter.is_user != null) p.set('is_user', String(filter.is_user));
      if (filter.newsletter != null) p.set('newsletter', String(filter.newsletter));
      if (filter.source) p.set('source', filter.source);
      p.set('limit', String(LIMIT)); p.set('offset', String(offset));
      const data = await adminFetch<{ total: number; contacts: Contact[] }>(`/api/admin/crm/contacts?${p}`);
      setContacts(data.contacts); setTotal(data.total);
    } catch (e: any) { onToast(e?.message || 'Failed to load contacts', 'error'); }
    finally { setLoading(false); }
  }, [filter, offset, onToast]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const applySearch = () => { setOffset(0); setFilter(f => ({ ...f, search: searchInput.trim() || undefined })); };
  const setTri = (key: 'is_user' | 'newsletter', val: boolean | null) => { setOffset(0); setFilter(f => ({ ...f, [key]: val })); };

  const triBtn = (active: boolean, label: string, onClick: () => void) => (
    <button onClick={onClick} style={{
      padding: '6px 12px', borderRadius: R.md, border: `1.5px solid ${active ? C.primary : C.border}`,
      background: active ? C.primary : C.surface, color: active ? '#fff' : C.textSecondary,
      cursor: 'pointer', fontSize: 13, fontWeight: 600,
    }}>{label}</button>
  );

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total contacts" value={stats?.total_contacts ?? '—'} />
        <StatCard label="App users" value={stats?.users ?? '—'} accent={C.primary} />
        <StatCard label="Not users" value={stats?.non_users ?? '—'} accent={C.purple} />
        <StatCard label="Subscribed" value={stats?.subscribed ?? '—'} accent={C.info} />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        marginBottom: 16, padding: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 220 }}>
          <Search size={16} color={C.textTertiary} />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applySearch(); }}
            placeholder="Search name or email…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: C.ink, background: 'transparent' }}
          />
          {searchInput && <X size={15} color={C.textTertiary} style={{ cursor: 'pointer' }} onClick={() => { setSearchInput(''); setOffset(0); setFilter(f => ({ ...f, search: undefined })); }} />}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {triBtn(filter.is_user == null, 'All', () => setTri('is_user', null))}
          {triBtn(filter.is_user === true, 'Users', () => setTri('is_user', true))}
          {triBtn(filter.is_user === false, 'Not users', () => setTri('is_user', false))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {triBtn(filter.newsletter == null, 'Any', () => setTri('newsletter', null))}
          {triBtn(filter.newsletter === true, 'Subscribed', () => setTri('newsletter', true))}
        </div>
        <button onClick={() => setShowSaveList(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: R.md,
          border: 'none', background: C.subtle, color: C.primary, cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}><Tag size={15} /> Save as list</button>
      </div>

      {/* Table */}
      {loading ? <Spinner /> : contacts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.textSecondary }}>No contacts match these filters.</div>
      ) : (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: C.limestone, textAlign: 'left' }}>
                {['Name', 'Email', 'Type', 'Newsletter', 'Source', 'Last activity'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contacts.map(ct => (
                <tr key={ct.id} onClick={() => setDetailId(ct.id)}
                  style={{ borderTop: `1px solid ${C.borderLight}`, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = C.borderLight)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ padding: '12px 16px', color: C.ink, fontWeight: 500 }}>
                    {(ct.first_name || ct.last_name) ? `${ct.first_name} ${ct.last_name}`.trim() : <span style={{ color: C.textTertiary }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.text }}>{ct.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {ct.is_user
                      ? <Pill bg={C.subtle} color={C.primary}>User</Pill>
                      : <Pill bg={C.purpleBg} color={C.purple}>Lead</Pill>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {ct.newsletter_subscribed
                      ? <Pill bg={C.successBg} color={C.success}>Subscribed</Pill>
                      : <span style={{ color: C.textTertiary, fontSize: 13 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.textSecondary, fontSize: 13 }}>
                    {SOURCE_LABELS[ct.newsletter_source || ct.first_lead_source || ''] || ct.newsletter_source || ct.first_lead_source || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.textSecondary, fontSize: 13 }}>
                    {ct.last_inquiry_at ? new Date(ct.last_inquiry_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ fontSize: 13, color: C.textSecondary }}>
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
              style={pageBtn(offset === 0)}><ChevronLeft size={16} /></button>
            <button disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}
              style={pageBtn(offset + LIMIT >= total)}><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {detailId && <ContactDetailDrawer contactId={detailId} onClose={() => setDetailId(null)} />}
      {showSaveList && (
        <CreateListModal
          presetFilter={filter}
          onClose={() => setShowSaveList(false)}
          onCreated={() => { setShowSaveList(false); loadStats(); onToast('Dynamic list saved from current filter', 'success'); }}
        />
      )}
    </div>
  );
};

const pageBtn = (disabled: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
  borderRadius: R.md, border: `1.5px solid ${C.border}`, background: C.surface,
  cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? C.textTertiary : C.text, opacity: disabled ? 0.5 : 1,
});

// ── Contact detail drawer ────────────────────────────────────────────────────
const ContactDetailDrawer: React.FC<{ contactId: string; onClose: () => void }> = ({ contactId, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await adminFetch<any>(`/api/admin/crm/contacts/${contactId}`)); }
      finally { setLoading(false); }
    })();
  }, [contactId]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,60,43,0.5)', zIndex: 9998, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 440, maxWidth: '92vw', height: '100%', background: C.surface, boxShadow: SH.lg,
        padding: 28, overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink }}>Contact</h3>
          <X size={20} color={C.textSecondary} style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>
        {loading || !data ? <Spinner /> : (
          <>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.ink }}>
              {(data.first_name || data.last_name) ? `${data.first_name} ${data.last_name}`.trim() : data.email}
            </div>
            <div style={{ fontSize: 14, color: C.textSecondary, marginBottom: 16 }}>{data.email}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {data.is_user ? <Pill bg={C.subtle} color={C.primary}>App user</Pill> : <Pill bg={C.purpleBg} color={C.purple}>Lead (not a user)</Pill>}
              {data.newsletter_subscribed && <Pill bg={C.successBg} color={C.success}>Subscribed</Pill>}
            </div>
            <DetailRow label="Newsletter source" value={SOURCE_LABELS[data.newsletter_source || ''] || data.newsletter_source || '—'} />
            <DetailRow label="First seen" value={data.first_seen_at ? new Date(data.first_seen_at).toLocaleString() : '—'} />
            <DetailRow label="First lead source" value={data.first_lead_source || '—'} />
            <DetailRow label="Inquiries" value={String(data.inquiry_count ?? 0)} />
            {data.lists?.length > 0 && (
              <DetailRow label="Lists" value={data.lists.map((l: any) => l.name).join(', ')} />
            )}
            {data.inquiries?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary, marginBottom: 8 }}>RECENT INQUIRIES</div>
                {data.inquiries.map((inq: any) => (
                  <div key={inq.id} style={{ padding: 12, background: C.limestone, borderRadius: R.md, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.primary, marginBottom: 4 }}>
                      {inq.inquiry_type} · {inq.created_at ? new Date(inq.created_at).toLocaleDateString() : ''}
                    </div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{inq.message}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.borderLight}` }}>
    <span style={{ fontSize: 13, color: C.textSecondary }}>{label}</span>
    <span style={{ fontSize: 13, color: C.ink, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
  </div>
);

// ── Create-list modal (dynamic from filter, or static empty) ─────────────────
const CreateListModal: React.FC<{
  presetFilter?: ContactFilter;
  onClose: () => void;
  onCreated: () => void;
}> = ({ presetFilter, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const isDynamic = !!presetFilter && Object.values(presetFilter).some(v => v != null && v !== '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await adminFetch('/api/admin/crm/lists', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          list_type: isDynamic ? 'dynamic' : 'static',
          filter_json: isDynamic ? presetFilter : null,
        }),
      });
      onCreated();
    } catch { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,60,43,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: R.xl, padding: 28, width: 440, maxWidth: '92vw', boxShadow: SH.lg }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: C.ink }}>
          {isDynamic ? 'Save dynamic list' : 'New list'}
        </h3>
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 20px', lineHeight: 1.5 }}>
          {isDynamic
            ? 'Auto-updating list of everyone matching your current filter. Membership recalculates whenever the list is used.'
            : 'An empty static list. Add contacts to it from the Contacts table.'}
        </p>
        <label style={labelStyle}>List name</label>
        <input value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="e.g. Newsletter — non-users" style={inputStyle} />
        <label style={labelStyle}>Description (optional)</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: R.md, border: `1.5px solid ${C.border}`, background: C.surface, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: C.text }}>Cancel</button>
          <button onClick={save} disabled={!name.trim() || saving} style={{ padding: '10px 20px', borderRadius: R.md, border: 'none', background: C.primary, color: '#fff', cursor: name.trim() && !saving ? 'pointer' : 'not-allowed', opacity: name.trim() && !saving ? 1 : 0.6, fontSize: 14, fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save list'}
          </button>
        </div>
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, marginTop: 12 };
const inputStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: R.md, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.ink, outline: 'none' };

// ════════════════════════════════════════════════════════════════════════════
//  LISTS SECTION
// ════════════════════════════════════════════════════════════════════════════
interface SavedList {
  id: string; name: string; description: string | null; list_type: string;
  member_count: number; created_at: string | null;
}

export const CrmListsSection: React.FC<{ onToast: Toast }> = ({ onToast }) => {
  const [lists, setLists] = useState<SavedList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openList, setOpenList] = useState<SavedList | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setLists((await adminFetch<{ lists: SavedList[] }>('/api/admin/crm/lists')).lists); }
    catch (e: any) { onToast(e?.message || 'Failed to load lists', 'error'); }
    finally { setLoading(false); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);

  const del = async (id: string) => {
    try { await adminFetch(`/api/admin/crm/lists/${id}`, { method: 'DELETE' }); onToast('List deleted', 'success'); load(); }
    catch (e: any) { onToast(e?.message || 'Delete failed', 'error'); }
  };

  if (openList) return <ListMembersView list={openList} onBack={() => { setOpenList(null); load(); }} onToast={onToast} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink }}>Saved lists</h3>
        <button onClick={() => setShowCreate(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: R.md,
          border: 'none', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}><Plus size={15} /> New list</button>
      </div>

      {loading ? <Spinner /> : lists.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.textSecondary }}>
          No saved lists yet. Create a static list here, or save a filter as a dynamic list from the Contacts tab.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {lists.map(l => (
            <div key={l.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg,
            }}>
              <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => setOpenList(l)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{l.name}</span>
                  <Pill bg={l.list_type === 'dynamic' ? C.infoBg : C.warningBg} color={l.list_type === 'dynamic' ? C.info : C.warning}>{l.list_type}</Pill>
                </div>
                {l.description && <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 4 }}>{l.description}</div>}
                <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 4 }}>{l.member_count} {l.member_count === 1 ? 'member' : 'members'}</div>
              </div>
              <button onClick={() => del(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textTertiary, padding: 8 }}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateListModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); onToast('List created', 'success'); }} />
      )}
    </div>
  );
};

// ── List members view ────────────────────────────────────────────────────────
const ListMembersView: React.FC<{ list: SavedList; onBack: () => void; onToast: Toast }> = ({ list, onBack, onToast }) => {
  const [members, setMembers] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetch<{ total: number; members: Contact[] }>(`/api/admin/crm/lists/${list.id}/members?limit=${LIMIT}&offset=${offset}`);
      setMembers(data.members); setTotal(data.total);
    } catch (e: any) { onToast(e?.message || 'Failed to load members', 'error'); }
    finally { setLoading(false); }
  }, [list.id, offset, onToast]);
  useEffect(() => { load(); }, [load]);

  const removeMember = async (contactId: string) => {
    try { await adminFetch(`/api/admin/crm/lists/${list.id}/members/${contactId}`, { method: 'DELETE' }); load(); }
    catch (e: any) { onToast(e?.message || 'Remove failed', 'error'); }
  };

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 }}>
        <ChevronLeft size={16} /> Back to lists
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink }}>{list.name}</h3>
        <Pill bg={list.list_type === 'dynamic' ? C.infoBg : C.warningBg} color={list.list_type === 'dynamic' ? C.info : C.warning}>{list.list_type}</Pill>
      </div>
      <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 20 }}>{total} {total === 1 ? 'member' : 'members'}{list.list_type === 'dynamic' ? ' (auto-updating)' : ''}</div>

      {loading ? <Spinner /> : members.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.textSecondary }}>No members yet.</div>
      ) : (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              {members.map(m => (
                <tr key={m.id} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                  <td style={{ padding: '12px 16px', color: C.ink, fontWeight: 500 }}>
                    {(m.first_name || m.last_name) ? `${m.first_name} ${m.last_name}`.trim() : <span style={{ color: C.textTertiary }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.text }}>{m.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {m.is_user ? <Pill bg={C.subtle} color={C.primary}>User</Pill> : <Pill bg={C.purpleBg} color={C.purple}>Lead</Pill>}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {list.list_type === 'static' && (
                      <button onClick={() => removeMember(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textTertiary, padding: 4 }}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ fontSize: 13, color: C.textSecondary }}>{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))} style={pageBtn(offset === 0)}><ChevronLeft size={16} /></button>
            <button disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)} style={pageBtn(offset + LIMIT >= total)}><ChevronRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
//  CAMPAIGNS SECTION (Phase 3) — compose → send to a saved list → opens
// ════════════════════════════════════════════════════════════════════════════
interface CampaignRow {
  id: string; name: string; subject: string; status: string;
  list_id: string | null; list_name: string | null;
  recipient_count: number; sent_count: number; created_at: string | null; sent_at: string | null;
}
interface CampaignDetailData extends CampaignRow {
  body_html: string;
  live_recipient_count: number | null;
  failed_count: number;
  stats: { delivered: number; unique_opens: number; unique_clicks: number };
}
interface ListLite { id: string; name: string; member_count: number; }

const STATUS_PILL: Record<string, { bg: string; color: string }> = {
  draft: { bg: C.limestone, color: C.textSecondary },
  sending: { bg: C.warningBg, color: C.warning },
  sent: { bg: C.successBg, color: C.success },
  failed: { bg: C.errorBg, color: C.error },
};

export const CrmCampaignsSection: React.FC<{ onToast: Toast }> = ({ onToast }) => {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'list' | 'new'>('list');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCampaigns((await adminFetch<{ campaigns: CampaignRow[] }>('/api/admin/crm/campaigns')).campaigns); }
    catch (e: any) { onToast(e?.message || 'Failed to load campaigns', 'error'); }
    finally { setLoading(false); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);

  if (openId) return <CampaignDetailView id={openId} onBack={() => { setOpenId(null); load(); }} onToast={onToast} />;
  if (mode === 'new') return <CampaignComposer onBack={() => setMode('list')} onCreated={() => { setMode('list'); load(); onToast('Campaign created as draft', 'success'); }} onToast={onToast} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink }}>Campaigns</h3>
        <button onClick={() => setMode('new')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: R.md, border: 'none', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <Plus size={15} /> New campaign
        </button>
      </div>

      {loading ? <Spinner /> : campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.textSecondary }}>
          No campaigns yet. Compose one and send it to a saved list.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {campaigns.map(ca => {
            const sp = STATUS_PILL[ca.status] || STATUS_PILL.draft;
            return (
              <div key={ca.id} onClick={() => setOpenId(ca.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg, cursor: 'pointer' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{ca.name}</span>
                    <Pill bg={sp.bg} color={sp.color}>{ca.status}</Pill>
                  </div>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 4 }}>{ca.subject}</div>
                  <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 4 }}>
                    {ca.list_name ? `List: ${ca.list_name}` : 'No list'}
                    {ca.status === 'sent' ? ` · ${ca.sent_count} sent` : ''}
                  </div>
                </div>
                <ChevronRight size={18} color={C.textTertiary} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CampaignComposer: React.FC<{ onBack: () => void; onCreated: () => void; onToast: Toast }> = ({ onBack, onCreated, onToast }) => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [listId, setListId] = useState('');
  const [lists, setLists] = useState<ListLite[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { setLists((await adminFetch<{ lists: ListLite[] }>('/api/admin/crm/lists')).lists); }
      catch { /* noop */ }
    })();
  }, []);

  const save = async () => {
    if (!name.trim() || !subject.trim() || !body.trim() || !listId) {
      onToast('Name, subject, body, and a list are all required', 'error');
      return;
    }
    setSaving(true);
    try {
      await adminFetch('/api/admin/crm/campaigns', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), subject: subject.trim(), body_html: body, list_id: listId }),
      });
      onCreated();
    } catch (e: any) { onToast(e?.message || 'Create failed', 'error'); setSaving(false); }
  };

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 }}>
        <ChevronLeft size={16} /> Back to campaigns
      </button>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: C.ink }}>New campaign</h3>
      <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 20px' }}>
        Compose, then send to a saved list. Use <code>{'{{first_name}}'}</code> in the body to personalize (falls back to "there").
      </p>

      <div style={{ maxWidth: 720 }}>
        <label style={labelStyle}>Campaign name <span style={{ color: C.textTertiary, fontWeight: 400 }}>(internal)</span></label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. June newsletter — non-users" style={inputStyle} />

        <label style={labelStyle}>Target list</label>
        <select value={listId} onChange={e => setListId(e.target.value)} style={inputStyle}>
          <option value="">Select a list…</option>
          {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.member_count})</option>)}
        </select>

        <label style={labelStyle}>Subject</label>
        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line" style={inputStyle} />

        <label style={labelStyle}>Body (HTML)</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} placeholder="<p>Hi {{first_name}},</p>..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onBack} style={{ padding: '10px 20px', borderRadius: R.md, border: `1.5px solid ${C.border}`, background: C.surface, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: C.text }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '10px 20px', borderRadius: R.md, border: 'none', background: C.primary, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, fontSize: 14, fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CampaignDetailView: React.FC<{ id: string; onBack: () => void; onToast: Toast }> = ({ id, onBack, onToast }) => {
  const [data, setData] = useState<CampaignDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminFetch<CampaignDetailData>(`/api/admin/crm/campaigns/${id}`)); }
    catch (e: any) { onToast(e?.message || 'Failed to load campaign', 'error'); }
    finally { setLoading(false); }
  }, [id, onToast]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true);
    try {
      const r = await adminFetch<{ recipient_count: number }>(`/api/admin/crm/campaigns/${id}/send`, { method: 'POST' });
      onToast(`Sending to ${r.recipient_count} recipients…`, 'success');
      setConfirmSend(false);
      setTimeout(load, 1500);
    } catch (e: any) { onToast(e?.message || 'Send failed', 'error'); }
    finally { setSending(false); }
  };

  const del = async () => {
    try { await adminFetch(`/api/admin/crm/campaigns/${id}`, { method: 'DELETE' }); onToast('Campaign deleted', 'success'); onBack(); }
    catch (e: any) { onToast(e?.message || 'Delete failed', 'error'); }
  };

  if (loading || !data) return <Spinner />;
  const sp = STATUS_PILL[data.status] || STATUS_PILL.draft;
  const isDraft = data.status === 'draft';

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 }}>
        <ChevronLeft size={16} /> Back to campaigns
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.ink }}>{data.name}</h3>
        <Pill bg={sp.bg} color={sp.color}>{data.status}</Pill>
      </div>
      <div style={{ fontSize: 14, color: C.textSecondary, marginBottom: 20 }}>
        {data.subject} · {data.list_name ? `List: ${data.list_name}` : 'No list'}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label={isDraft ? 'Will send to' : 'Recipients'} value={isDraft ? (data.live_recipient_count ?? '—') : data.recipient_count} />
        <StatCard label="Sent" value={data.sent_count} accent={C.primary} />
        <StatCard label="Delivered" value={data.stats.delivered} />
        <StatCard label="Opened" value={data.stats.unique_opens} accent={C.info} />
        <StatCard label="Clicked" value={data.stats.unique_clicks} accent={C.purple} />
      </div>

      {/* Body preview */}
      <div style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary, marginBottom: 8 }}>EMAIL PREVIEW</div>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: R.lg, padding: 20, background: C.surface, maxWidth: 640, maxHeight: 320, overflow: 'auto' }}
        dangerouslySetInnerHTML={{ __html: data.body_html }} />

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
        {isDraft && (
          <button onClick={() => setConfirmSend(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 22px', borderRadius: R.md, border: 'none', background: C.primary, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            <Send size={15} /> Send campaign
          </button>
        )}
        {data.status !== 'sending' && (
          <button onClick={del} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 18px', borderRadius: R.md, border: `1.5px solid ${C.border}`, background: C.surface, color: C.textSecondary, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            <Trash2 size={15} /> Delete
          </button>
        )}
      </div>

      {confirmSend && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,60,43,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setConfirmSend(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: R.xl, padding: 28, width: 440, maxWidth: '92vw', boxShadow: SH.lg }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: C.ink }}>Send this campaign?</h3>
            <p style={{ fontSize: 14, color: C.textSecondary, margin: '0 0 20px', lineHeight: 1.5 }}>
              This sends "<strong>{data.subject}</strong>" to <strong>{data.live_recipient_count ?? '—'}</strong> opted-in, non-suppressed contacts in <strong>{data.list_name}</strong>. This can't be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setConfirmSend(false)} style={{ padding: '10px 20px', borderRadius: R.md, border: `1.5px solid ${C.border}`, background: C.surface, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: C.text }}>Cancel</button>
              <button onClick={send} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: R.md, border: 'none', background: C.primary, color: '#fff', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1, fontSize: 14, fontWeight: 600 }}>
                <Send size={15} /> {sending ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
